function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useRef, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { Options, Point, TooltipFormatterCallbackFunction } from 'highcharts';
import sum from 'lodash/sum';
import ResizeObserver from 'resize-observer-polyfill';
import isEqual from 'lodash/isEqual';
import { truncateMiddle } from 'common/util/string';
import { getChartSegmentColorCombo } from 'common/util/colors';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import { HoveredItem } from 'common/charts/contexts/segment-filter-controller';
import { getSeriesMatchPath } from 'common/charts/contexts/utils';
import { withStyles } from 'common/util/react';
import { useSegmentFilterController } from 'common/contexts/react/segment-filter-controller-context';

// Still using inline styles because we need to figure out how to get css modules working with Highcharts.
import css from './index.inline.styl';
import { ChartDataItem, PieChartProps, TooltipFormatterParams } from './types';
import { defaultTooltipFormatter, setPieSeriesState } from './util';
const PieChart = (props: PieChartProps) => {
  var _props$getMetricKeyFo, _props$valueFormatter;
  const [chartData, setChartData] = useState<ChartDataItem[]>(props.chartData);
  const [chartHeight, setChartHeight] = useState<number>(0);
  const [highcharts, setHighcharts] = useState<Highcharts.Chart | null>(null);
  const [resizeObserver, setResizeObserver] = useState<ResizeObserver | null>(null);
  const [total, setTotal] = useState<number>(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const segmentFilterController = useSegmentFilterController();
  const isSmall = chartHeight <= 300;
  const getMetricKeyForName = (_props$getMetricKeyFo = props.getMetricKeyForName) !== null && _props$getMetricKeyFo !== void 0 ? _props$getMetricKeyFo : () => "";
  const valueFormatter = (_props$valueFormatter = props.valueFormatter) !== null && _props$valueFormatter !== void 0 ? _props$valueFormatter : (_metricKey, value) => value + "";
  const seriesChartData = useMemo((): ChartDataItem[] => {
    return chartData.map((item, idx) => {
      const metricKey = getMetricKeyForName(item.name);
      return _objectSpread(_objectSpread({}, item), {}, {
        color: item.color || getChartSegmentColorCombo(idx).primary,
        // assign default primary colors if there's none
        id: item.name,
        metricKey,
        name: item.name,
        percent: item.percent,
        secondaryColor: item.secondaryColor || getChartSegmentColorCombo(idx).secondary,
        // assign default secondary colors if there's none
        value: +item.value,
        y: item.pieChartValue || item.value
      });
    });
  }, [chartData, getMetricKeyForName]);
  const tooltipFormatter = useMemo(() => {
    const format: TooltipFormatterCallbackFunction = function format() {
      const params: TooltipFormatterParams = {
        name: this.key!,
        value: this.point.y!,
        percent: this.point.percentage!,
        color: this.point.color as string,
        point: this.point
      };
      return props.tooltipFormatter ? props.tooltipFormatter(params) : defaultTooltipFormatter(params);
    };
    return format;
  }, [props.tooltipFormatter]);
  const updateChart = () => {
    // Make sure to check the presence of "options". If destroy has been called
    // in the same tick as updateChart, highcharts won't be null yet but the
    // chart will already be destroyed and cause an error.
    if (highcharts && highcharts.options) {
      highcharts.reflow();
    }
  };

  /** Hovers a pie slice (highcharts point) when the corresponding data table row is hovered */
  const tableHovered = (hovered: HoveredItem | null) => {
    var _hoveredPoints;
    if (!highcharts) {
      return;
    }
    if (highcharts.container.style.pointerEvents === "none") {
      // the chart is disabled, like when a popover is open
      return;
    }
    let hoveredPoints: Point[] | null = null;
    if (hovered !== null && hovered !== void 0 && hovered.seriesId && hovered.partialMatch) {
      const matchPath = getSeriesMatchPath(hovered.seriesId);
      hoveredPoints = highcharts.series[0].points.filter(s => getSeriesMatchPath(s.name) === matchPath);
    } else if (hovered !== null && hovered !== void 0 && hovered.seriesId) {
      const point = highcharts.get(hovered.seriesId) as Point;
      hoveredPoints = point ? [point] : [];
    }
    if ((_hoveredPoints = hoveredPoints) !== null && _hoveredPoints !== void 0 && _hoveredPoints.length) {
      setPieSeriesState("hover", highcharts.series[0], hoveredPoints);
    } else {
      setPieSeriesState("normal", highcharts.series[0]);
    }
  };
  useEffect(() => {
    return () => {
      resizeObserver === null || resizeObserver === void 0 || resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!rootRef.current) {
      return;
    }
    const selfResizeObserver = new ResizeObserver(entries => {
      const {
        height
      } = entries[0].contentRect;
      setChartHeight(height);
      updateChart();
    });
    selfResizeObserver.observe(rootRef.current);
    setResizeObserver(selfResizeObserver);
  }, [rootRef.current]);
  useEffect(() => {
    tableHovered(segmentFilterController === null || segmentFilterController === void 0 ? void 0 : segmentFilterController.hoveredSeries);
  }, [segmentFilterController === null || segmentFilterController === void 0 ? void 0 : segmentFilterController.hoveredSeries]);
  useEffect(() => {
    if (!isEqual(chartData, props.chartData)) {
      setChartData(props.chartData);
    }
  }, [props.chartData]);
  useEffect(() => {
    setTotal(sum(chartData.map(item => item.value)));
  }, [chartData]);
  return <div className={props.className} elref='pieChart' ref={rootRef} style={{
    height: "100%",
    width: "100%"
  }} data-sentry-component="PieChart" data-sentry-source-file="index.tsx">
      <HighchartsReact callback={(c: Highcharts.Chart) => setHighcharts(c)} containerProps={{
      style: {
        height: "100%",
        width: "100%"
      },
      className: "mp-highcharts-root"
    }} highcharts={Highcharts} options={((): Options => ({
      // TODO consider using the accessibility.js module. This silences warning in the meantime.
      accessibility: {
        enabled: false
      },
      chart: {
        animation: false,
        backgroundColor: undefined,
        type: "pie",
        style: {
          fontFamily: mpFonts.fontFamilyPrimary
        },
        margin: isSmall ? 8 : 64
      },
      credits: {
        enabled: false
      },
      title: {
        align: "center",
        style: {
          color: undefined,
          fill: undefined,
          fontSize: isSmall ? "19px" : undefined
        },
        text: valueFormatter("", total),
        verticalAlign: "middle"
      },
      plotOptions: {
        pie: _objectSpread(_objectSpread({}, props.onPieChartClick && {
          cursor: "pointer"
        }), {}, {
          dataLabels: {
            style: {
              color: undefined,
              fill: undefined,
              fontSize: undefined,
              textOutline: "none"
            },
            color: undefined,
            enabled: !isSmall,
            formatter(this) {
              var _options$displayName;
              const options: Highcharts.PointOptionsObject & ChartDataItem = this.point.options as any;
              const label = (_options$displayName = options.displayName) !== null && _options$displayName !== void 0 ? _options$displayName : this.key;
              const metricKey = options.metricKey;
              const formatted = valueFormatter(metricKey, this.y);
              return "\n                <div class=\"pie-segment-label\">\n                  ".concat(truncateMiddle(label, 30), "\n                  <br />\n                  <div class=\"value\">").concat(formatted, "</div>\n                </div>\n              ");
            }
          },
          shadow: false,
          size: "100%",
          slicedOffset: 0
        }),
        series: {
          animation: false,
          states: {
            inactive: {
              animation: false,
              enabled: true,
              opacity: 0.3
            }
          },
          point: {
            events: {
              click: event => {
                var _props$onPieChartClic;
                return (_props$onPieChartClic = props.onPieChartClick) === null || _props$onPieChartClic === void 0 ? void 0 : _props$onPieChartClic.call(props, event.point);
              },
              mouseOut() {
                segmentFilterController === null || segmentFilterController === void 0 || segmentFilterController.notifySeriesHovered(null);
              },
              mouseOver() {
                segmentFilterController === null || segmentFilterController === void 0 || segmentFilterController.notifySeriesHovered({
                  seriesId: this.name
                });
              }
            }
          }
        }
      },
      tooltip: {
        backgroundColor: undefined,
        padding: 0,
        borderRadius: 0,
        borderWidth: 0,
        formatter: tooltipFormatter,
        hideDelay: 0,
        shadow: false,
        useHTML: true
      },
      series: [{
        type: "pie",
        data: seriesChartData,
        innerSize: "60%"
      }]
    }))()} data-sentry-element="HighchartsReact" data-sentry-source-file="index.tsx" />
    </div>;
};
export const PieChartReact = withStyles(PieChart, css);